import React, { useState, useEffect, useCallback } from 'react';
import { StepProps } from '../MultiStepModal';
import { useToast } from '../../../contexts/ToastContext';
import { clientsApi } from '../../../utils/api';
import { UserIcon, BuildingOfficeIcon, PlusIcon } from '@heroicons/react/24/outline';

interface Client {
  id: string;
  name: string;
  email?: string;
  phone: string;
  address: string;
  postcode?: string;
}

export default function CustomerInfoStep({ data, onDataChange, onNext }: StepProps) {
  const { showToast } = useToast();
  const [isNewCustomer, setIsNewCustomer] = useState(data.isNewCustomer || false);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clientsApi.getAll();
      const payload = response.data;
      const list = Array.isArray(payload) ? payload : payload?.data;
      setClients(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      showToast('Failed to load clients', 'error');
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleInputChange = (field: string, value: string) => {
    onDataChange({ [field]: value });
  };

  const handleCustomerSelect = (customerId: string) => {
    const customer = clients.find(c => c.id === customerId);
    if (customer) {
      onDataChange({
        customerId,
        customerName: customer.name,
        customerEmail: customer.email || '',
        customerPhone: customer.phone,
        customerAddress: customer.address,
        postcode: customer.postcode || '',
        isNewCustomer: false
      });
    }
  };

  const handleNewCustomerToggle = () => {
    setIsNewCustomer(!isNewCustomer);
    onDataChange({ 
      isNewCustomer: !isNewCustomer,
      customerId: '',
      customerName: '',
      customerCompany: '',
      customerAddress: '',
      customerPhone: '',
      customerEmail: ''
    });
  };

  const validateForm = () => {
    if (isNewCustomer) {
      if (!data.customerName?.trim()) {
        showToast('Please enter customer name', 'error');
        return false;
      }
      if (!data.customerPhone?.trim()) {
        showToast('Please enter customer phone number', 'error');
        return false;
      }
      if (!data.customerAddress?.trim()) {
        showToast('Please enter customer address', 'error');
        return false;
      }
    } else {
      if (!data.customerId) {
        showToast('Please select a customer', 'error');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">Customer Information</h3>
        <p className="step-form-subtitle">Select an existing customer or add a new one</p>
      </div>

      <form onSubmit={handleSubmit} className="step-form-content">
        <div className="form-group">
          <div className="customer-type-toggle">
            <button
              type="button"
              className={`toggle-btn ${!isNewCustomer ? 'active' : ''}`}
              onClick={() => !isNewCustomer || handleNewCustomerToggle()}
            >
              <UserIcon className="w-4 h-4 mr-2" />
              Existing Customer
            </button>
            <button
              type="button"
              className={`toggle-btn ${isNewCustomer ? 'active' : ''}`}
              onClick={() => isNewCustomer || handleNewCustomerToggle()}
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              New Customer
            </button>
          </div>
        </div>

        {!isNewCustomer ? (
          <div className="form-group">
            <label className="form-label">Select Customer *</label>
            {loading ? (
              <div className="loading-state">
                <p>Loading customers...</p>
              </div>
            ) : clients.length > 0 ? (
              <div className="customer-grid">
                {clients.map((customer) => (
                  <div
                    key={customer.id}
                    className={`customer-card ${data.customerId === customer.id ? 'selected' : ''}`}
                    onClick={() => handleCustomerSelect(customer.id)}
                  >
                    <div className="customer-avatar">
                      <UserIcon className="w-5 h-5" />
                    </div>
                    <div className="customer-info">
                      <h4 className="customer-name">{customer.name}</h4>
                      <p className="customer-phone">{customer.phone}</p>
                      <p className="customer-address">{customer.address}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No customers found. Create your first customer below.</p>
                <button
                  type="button"
                  className="btn-primary mt-2"
                  onClick={handleNewCustomerToggle}
                >
                  Add New Customer
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Customer Name *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="John Smith"
                  value={data.customerName || ''}
                  onChange={(e) => handleInputChange('customerName', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Company</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Smith Properties Ltd"
                  value={data.customerCompany || ''}
                  onChange={(e) => handleInputChange('customerCompany', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Phone Number *</label>
                <input
                  type="tel"
                  className="form-input"
                  placeholder="+44 20 7123 4567"
                  value={data.customerPhone || ''}
                  onChange={(e) => handleInputChange('customerPhone', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="john@example.com"
                  value={data.customerEmail || ''}
                  onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <BuildingOfficeIcon className="w-4 h-4 inline mr-2" />
                Property Address *
              </label>
              <textarea
                className="form-textarea"
                placeholder="123 Oak Street, London, SW1A 1AA"
                value={data.customerAddress || ''}
                onChange={(e) => handleInputChange('customerAddress', e.target.value)}
                rows={2}
                required
              />
            </div>
          </>
        )}
      </form>
    </div>
  );
}
